import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, NavLink } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { DemoUserSwitcher } from "@/components/DemoUserSwitcher";
import { AuthedAvatar } from "@/attendee/AuthedAvatar";
import { initials } from "@/lib/format";
import { Home, CalendarDays, Compass, Trophy, Menu, ArrowLeft, Brain } from "lucide-react";

const BASE_TABS = [
  { to: "/app", label: "Home", icon: Home, testid: "attendee-bottom-nav-home", end: true },
  { to: "/app/schedule", label: "Schedule", icon: CalendarDays, testid: "attendee-bottom-nav-schedule" },
  { to: "/app/explore", label: "Explore", icon: Compass, testid: "attendee-bottom-nav-explore" },
];
const CHALLENGE_TAB = { to: "/app/challenge", label: "Challenge", icon: Trophy, testid: "attendee-bottom-nav-challenge" };
const QUEST_TAB = { to: "/app/quest", label: "PC Quest", icon: Brain, testid: "attendee-bottom-nav-quest" };
const MORE_TAB = { to: "/app/more", label: "More", icon: Menu, testid: "attendee-bottom-nav-more" };

function ProfileChip({ persona }) {
  const navigate = useNavigate();
  const { myProfile, attendeeId } = useApp();
  if (!persona) return null;
  // Prefer the attendee's self-saved display name + photo; fall back to the
  // official attendee record. Official records are never modified.
  const savedName = myProfile?.display_name_is_custom ? myProfile.display_name : null;
  const fullName = savedName || `${persona.display_first_name || persona.first_name || ""} ${persona.last_name || ""}`.trim() || "Guest";
  const first = (savedName ? savedName.split(/\s+/)[0] : (persona.display_first_name || persona.first_name)) || "Guest";
  const photoUrl = myProfile?.photo_url || null;       // protected self-profile photo
  const plainAvatar = persona.avatar || null;          // demo persona avatar (dev-only)
  return (
    <button
      data-testid="attendee-profile-chip"
      onClick={() => navigate("/app/profile")}
      className="flex items-center gap-2 rounded-full bg-white/90 backdrop-blur border border-white/70 pl-1.5 pr-3 py-1.5 shadow-[0_8px_24px_hsl(189_62%_4%/0.5)] hover:bg-white transition-colors"
    >
      {photoUrl ? (
        <AuthedAvatar photoUrl={photoUrl} name={fullName} identityKey={attendeeId} className="h-8 w-8" textClass="text-xs" />
      ) : plainAvatar ? (
        <img src={plainAvatar} alt={first} className="h-8 w-8 rounded-full object-cover" />
      ) : (
        <div className="h-8 w-8 rounded-full bg-[hsl(var(--secondary))] text-white grid place-items-center text-xs font-semibold">
          {initials(fullName)}
        </div>
      )}
      <span className="text-sm font-semibold text-[hsl(var(--primary))] max-w-[7.5rem] truncate">
        {first}
      </span>
    </button>
  );
}

export default function AttendeeLayout() {
  const { loading, demoMode, currentPersona, eventId, previewTestEvent } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/app";
  const [gamVisible, setGamVisible] = useState(false);
  const [questVisible, setQuestVisible] = useState(false);
  const [featureVis, setFeatureVis] = useState({});

  useEffect(() => {
    let ok = true;
    const eid = eventId || "presidents-club-2026";
    api.gamVisible(eid).then((r) => { if (ok) setGamVisible(!!r.visible); }).catch(() => {});
    api.questVisible(eid).then((r) => { if (ok) setQuestVisible(!!r.visible); }).catch(() => {});
    api.getEvent(eid).then((ev) => { if (ok) setFeatureVis(ev?.feature_visibility || {}); }).catch(() => {});
    return () => { ok = false; };
  }, [eventId]);

  const on = (k) => featureVis[k] !== false; // default visible unless explicitly disabled
  const baseTabs = [BASE_TABS[0], ...(on("schedule") ? [BASE_TABS[1]] : []), ...(on("explore") ? [BASE_TABS[2]] : [])];
  const midTabs = [
    ...((gamVisible && on("challenge")) ? [CHALLENGE_TAB] : []),
    ...(questVisible ? [QUEST_TAB] : []),
  ];
  const TABS = [...baseTabs, ...midTabs, MORE_TAB];

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]">
        <div className="animate-pulse text-[hsl(var(--muted-foreground))] font-serif text-xl">
          Loading your experience…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--pc-ink))] flex justify-center">
      {/* Device column */}
      <div className="att-bg relative w-full max-w-[30rem] min-h-screen shadow-2xl overflow-hidden">
        {/* Floating top controls */}
        <div className="pointer-events-none absolute top-0 inset-x-0 z-40">
          <div className="pointer-events-auto flex items-center justify-between px-4 pt-4">
            {isHome ? (
              <span />
            ) : (
              <button
                data-testid="attendee-back-button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 rounded-full att-glass px-3 py-1.5 text-xs font-semibold text-cream shadow-sm"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            {demoMode ? <DemoUserSwitcher variant="floating" /> : <ProfileChip persona={currentPersona} />}
          </div>
        </div>

        {/* Page content */}
        <main className="pb-24 min-h-screen text-cream">
          {previewTestEvent && (
            <div data-testid="preview-test-banner" className="sticky top-0 z-30 bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))] text-center text-[11px] font-bold uppercase tracking-[0.18em] py-1.5">
              Preview Test Event — not real data
            </div>
          )}
          <Outlet />
        </main>

        {/* Bottom navigation */}
        <nav
          data-testid="attendee-bottom-nav"
          className="absolute bottom-0 inset-x-0 z-40 att-glass border-t border-[hsl(var(--pc-line))] text-cream"
        >
          <div className="grid px-1 pt-1.5 pb-2" style={{ gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))` }}>
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  data-testid={t.testid}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 py-1.5 rounded-xl transition-colors ${
                      isActive ? "text-[hsl(var(--pc-gold))]" : "text-[hsl(var(--pc-cream)/0.55)] hover:text-[hsl(var(--pc-cream)/0.85)]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 1.9} />
                      <span className="text-[10px] font-semibold tracking-wide">{t.label}</span>
                      <span
                        className={`h-1 w-1 rounded-full transition-all ${
                          isActive ? "bg-[hsl(var(--pc-gold))]" : "bg-transparent"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
